import React from "react";
import { LegalPage } from "../components/LegalPage";

// Verbatim text from the official Data Processing Agreement (DPA).
// DO NOT rewrite, summarise, or paraphrase any wording below.
const sections = [
  {
    id: "sec-1",
    number: "1.",
    title: "PURPOSE",
    body: [
      { type: "sub", number: "1.1", title: "Purpose" },
      {
        type: "p",
        text: "This Data Processing Agreement (\u201CDPA\u201D) forms part of the Master Service Agreement (\u201CMSA\u201D) between MiseOne (\u201CProcessor\u201D) and the Customer (\u201CController\u201D).",
      },
      {
        type: "p",
        text: "Its purpose is to define the terms under which MiseOne processes Personal Data on behalf of the Customer in accordance with the General Data Protection Regulation (EU) 2016/679 (\u201CGDPR\u201D) and other applicable data protection laws.",
      },

      { type: "sub", number: "1.2", title: "Relationship with the MSA" },
      {
        type: "p",
        text: "This DPA supplements the MSA and applies whenever MiseOne processes Personal Data on behalf of the Customer while providing the Services.",
      },
      {
        type: "p",
        text: "Except where expressly stated otherwise, all terms defined in the MSA have the same meaning in this DPA.",
      },

      { type: "sub", number: "1.3", title: "Order of Precedence" },
      {
        type: "p",
        text: "If there is any conflict between this DPA and the MSA regarding the processing of Personal Data, this DPA shall prevail.",
      },
      { type: "p", text: "For all other matters, the MSA shall govern." },
    ],
  },
  {
    id: "sec-2",
    number: "2.",
    title: "DEFINITIONS",
    body: [
      { type: "p", text: "For the purposes of this DPA, the following terms have the meanings set out below." },
      {
        type: "p",
        text: "Controller means the entity that determines the purposes and means of processing Personal Data.",
      },
      {
        type: "p",
        text: "Processor means the entity that processes Personal Data on behalf of the Controller.",
      },
      {
        type: "p",
        text: "Personal Data means any information relating to an identified or identifiable natural person, as defined by the GDPR.",
      },
      {
        type: "p",
        text: "Processing means any operation performed on Personal Data, including collection, storage, organisation, use, disclosure, transmission, deletion, or destruction.",
      },
      { type: "p", text: "Data Subject means the individual to whom the Personal Data relates." },
      {
        type: "p",
        text: "Capitalised terms not defined in this DPA have the meanings given in the MSA or the GDPR.",
      },
    ],
  },
  {
    id: "sec-3",
    number: "3.",
    title: "SCOPE",
    body: [
      { type: "sub", number: "3.1", title: "Applicability" },
      {
        type: "p",
        text: "This DPA applies whenever MiseOne processes Personal Data on behalf of the Customer in connection with the Services.",
      },

      { type: "sub", number: "3.2", title: "Duration" },
      {
        type: "p",
        text: "This DPA remains in effect for as long as MiseOne processes Personal Data on behalf of the Customer.",
      },

      { type: "sub", number: "3.3", title: "Nature of the Processing" },
      { type: "p", text: "MiseOne processes Personal Data only to:" },
      {
        type: "ul",
        items: [
          "provide the Services;",
          "maintain the Platform;",
          "provide customer support;",
          "secure the Platform;",
          "perform backups;",
          "comply with legal obligations.",
        ],
      },
    ],
  },
  {
    id: "sec-4",
    number: "4.",
    title: "ROLES OF THE PARTIES",
    body: [
      { type: "sub", number: "4.1", title: "Customer as Controller" },
      {
        type: "p",
        text: "The Customer acts as the Data Controller and is solely responsible for determining the purposes and means of processing Personal Data through the Services.",
      },
      {
        type: "p",
        text: "The Customer is responsible for ensuring that all Personal Data processed through the Platform has a valid legal basis under applicable data protection laws.",
      },

      { type: "sub", number: "4.2", title: "MiseOne as Processor" },
      {
        type: "p",
        text: "MiseOne acts as the Data Processor and shall process Personal Data only on behalf of the Customer and in accordance with:",
      },
      {
        type: "ul",
        items: [
          "this DPA;",
          "the MSA;",
          "the Customer's documented instructions;",
          "applicable law.",
        ],
      },

      { type: "sub", number: "4.3", title: "Compliance with Instructions" },
      {
        type: "p",
        text: "If MiseOne reasonably believes that an instruction from the Customer infringes applicable data protection law, MiseOne may suspend the relevant processing and notify the Customer without undue delay.",
      },
    ],
  },
  {
    id: "sec-5",
    number: "5.",
    title: "PROCESSING OF PERSONAL DATA",
    body: [
      { type: "sub", number: "5.1", title: "Processing Activities" },
      { type: "p", text: "MiseOne may process Personal Data only to the extent necessary to:" },
      {
        type: "ul",
        items: [
          "provide the Services;",
          "authenticate Users;",
          "maintain Customer accounts;",
          "store and organise Customer Data;",
          "provide technical support;",
          "secure the Platform;",
          "create encrypted backups;",
          "monitor system performance;",
          "comply with applicable legal obligations.",
        ],
      },

      { type: "sub", number: "5.2", title: "No Unauthorised Use" },
      { type: "p", text: "MiseOne shall not:" },
      {
        type: "ul",
        items: [
          "sell Personal Data;",
          "use Personal Data for advertising;",
          "disclose Personal Data except as permitted by this DPA or required by law;",
          "process Personal Data for its own independent commercial purposes.",
        ],
      },

      { type: "sub", number: "5.3", title: "Confidentiality" },
      {
        type: "p",
        text: "All personnel authorised to process Personal Data on behalf of MiseOne shall be bound by appropriate confidentiality obligations.",
      },
      {
        type: "p",
        text: "Access to Personal Data shall be limited to personnel who require such access for the provision of the Services.",
      },
    ],
  },
  {
    id: "sec-6",
    number: "6.",
    title: "CATEGORIES OF PERSONAL DATA",
    body: [
      {
        type: "p",
        text: "Depending on how the Customer uses the Platform, Personal Data may include:",
      },
      {
        type: "ul",
        items: [
          "names;",
          "business email addresses;",
          "business telephone numbers;",
          "user account information;",
          "login identifiers;",
          "role and permission information;",
          "activity logs;",
          "support communications;",
          "operational records entered by the Customer.",
        ],
      },
      {
        type: "p",
        text: "MiseOne does not intentionally require the processing of special categories of Personal Data under Article 9 GDPR.",
      },
      {
        type: "p",
        text: "The Customer shall avoid storing such data within the Platform unless strictly necessary and legally permitted.",
      },
    ],
  },
  {
    id: "sec-7",
    number: "7.",
    title: "DATA SUBJECTS",
    body: [
      { type: "p", text: "The categories of Data Subjects may include:" },
      {
        type: "ul",
        items: [
          "employees;",
          "managers;",
          "administrators;",
          "authorised Users;",
          "contractors;",
          "suppliers;",
          "business contacts;",
          "other individuals whose Personal Data is entered into the Platform by the Customer.",
        ],
      },
    ],
  },
  {
    id: "sec-8",
    number: "8.",
    title: "SECURITY MEASURES",
    body: [
      { type: "sub", number: "8.1", title: "Technical and Organisational Measures" },
      {
        type: "p",
        text: "MiseOne shall implement and maintain appropriate technical and organisational measures designed to protect Personal Data against accidental or unlawful destruction, loss, alteration, unauthorised disclosure, or access.",
      },
      {
        type: "p",
        text: "Such measures shall take into account the nature of the Services, the risks presented by the processing, and the current state of technology.",
      },

      { type: "sub", number: "8.2", title: "Security Controls" },
      { type: "p", text: "The security measures implemented by MiseOne may include, where applicable:" },
      {
        type: "ul",
        items: [
          "encrypted communications using HTTPS/TLS;",
          "encryption of stored data through enterprise-grade cloud infrastructure;",
          "Role-Based Access Control (RBAC);",
          "logical separation of Customer environments (tenant isolation);",
          "audit logging of administrative activities;",
          "automated backups;",
          "controlled administrative access;",
          "regular maintenance and security updates;",
          "monitoring of the production environment.",
        ],
      },
      {
        type: "p",
        text: "MiseOne may improve or replace these measures over time without reducing the overall level of protection.",
      },

      { type: "sub", number: "8.3", title: "Personnel Access" },
      {
        type: "p",
        text: "Access to Personal Data is limited to authorised personnel who require access to perform their duties.",
      },
      { type: "p", text: "All authorised personnel are subject to confidentiality obligations." },
    ],
  },
  {
    id: "sec-9",
    number: "9.",
    title: "SUBPROCESSORS",
    body: [
      { type: "sub", number: "9.1", title: "Use of Subprocessors" },
      {
        type: "p",
        text: "The Customer authorises MiseOne to engage trusted subprocessors where reasonably necessary to provide the Services.",
      },
      {
        type: "p",
        text: "MiseOne shall remain responsible for the performance of its obligations under this DPA where required by applicable law.",
      },

      { type: "sub", number: "9.2", title: "Changes to Subprocessors" },
      { type: "p", text: "MiseOne may update its list of subprocessors from time to time." },
      { type: "p", text: "An up-to-date list shall be made available to Customers." },
      {
        type: "p",
        text: "Where required by applicable law, Customers shall be informed of material changes.",
      },
    ],
  },
  {
    id: "sec-10",
    number: "10.",
    title: "INTERNATIONAL DATA TRANSFERS",
    body: [
      {
        type: "p",
        text: "If Personal Data is transferred outside the European Economic Area (EEA), MiseOne shall ensure that appropriate safeguards are implemented in accordance with the GDPR.",
      },
      { type: "p", text: "Such safeguards may include:" },
      {
        type: "ul",
        items: [
          "Standard Contractual Clauses (SCCs);",
          "adequacy decisions;",
          "or any other lawful transfer mechanism recognised under applicable law.",
        ],
      },
    ],
  },
  {
    id: "sec-11",
    number: "11.",
    title: "PERSONAL DATA BREACHES",
    body: [
      {
        type: "p",
        text: "If MiseOne becomes aware of a Personal Data Breach affecting Personal Data processed on behalf of the Customer, MiseOne shall:",
      },
      {
        type: "ul",
        items: [
          "investigate the incident;",
          "take reasonable measures to contain and mitigate the incident;",
          "notify the Customer without undue delay where required by applicable law;",
          "provide available information necessary for the Customer to fulfil its own legal obligations.",
        ],
      },
    ],
  },
  {
    id: "sec-12",
    number: "12.",
    title: "DATA SUBJECT REQUESTS",
    body: [
      { type: "sub", number: "12.1", title: "Assistance" },
      {
        type: "p",
        text: "Where reasonably possible and taking into account the nature of the processing, MiseOne shall provide reasonable assistance to the Customer in responding to requests from Data Subjects under applicable data protection law.",
      },
      {
        type: "p",
        text: "Such assistance may be subject to reasonable technical limitations and additional fees where substantial manual work is required.",
      },

      { type: "sub", number: "12.2", title: "Direct Requests" },
      {
        type: "p",
        text: "If MiseOne receives a request directly from a Data Subject relating to Personal Data processed on behalf of the Customer, MiseOne shall, unless prohibited by law:",
      },
      {
        type: "ul",
        items: [
          "promptly notify the Customer; or",
          "direct the Data Subject to the Customer.",
        ],
      },
      {
        type: "p",
        text: "MiseOne shall not respond to such requests except where required by applicable law or authorised by the Customer.",
      },
    ],
  },
  {
    id: "sec-13",
    number: "13.",
    title: "AUDITS",
    body: [
      { type: "sub", number: "13.1", title: "Information Requests" },
      {
        type: "p",
        text: "Upon reasonable written request, MiseOne shall provide information reasonably necessary to demonstrate compliance with this DPA.",
      },

      { type: "sub", number: "13.2", title: "Audit Limitations" },
      { type: "p", text: "Any audit shall:" },
      {
        type: "ul",
        items: [
          "be conducted during normal business hours;",
          "be subject to reasonable prior notice;",
          "avoid disruption to the Services;",
          "protect the confidential information and security of MiseOne and its other customers.",
        ],
      },
      {
        type: "p",
        text: "Where appropriate, existing compliance documentation, independent audit reports, or security documentation may be provided instead of an on-site audit.",
      },
    ],
  },
  {
    id: "sec-14",
    number: "14.",
    title: "RETURN & DELETION OF PERSONAL DATA",
    body: [
      {
        type: "p",
        text: "Upon termination of the Services, the Customer may request the return or export of Personal Data during the applicable retention period.",
      },
      {
        type: "p",
        text: "Following that period, MiseOne may securely delete or anonymise Personal Data unless a longer retention period is required by applicable law.",
      },
    ],
  },
  {
    id: "sec-15",
    number: "15.",
    title: "LIABILITY",
    body: [
      {
        type: "p",
        text: "Liability arising from the processing of Personal Data shall be governed by the liability provisions set out in the Master Service Agreement, except where the GDPR or other mandatory applicable law provides otherwise.",
      },
    ],
  },
  {
    id: "sec-16",
    number: "16.",
    title: "TERM",
    body: [
      {
        type: "p",
        text: "This DPA enters into force on the same Effective Date as the Master Service Agreement and remains in effect for as long as MiseOne processes Personal Data on behalf of the Customer.",
      },
    ],
  },
  {
    id: "annex-1",
    number: "Annex I",
    title: "Description of Processing",
    body: [
      {
        type: "table",
        rows: [
          ["Item", "Description"],
          ["Controller", "Customer"],
          ["Processor", "MiseOne"],
          ["Purpose", "Provision of the Services"],
          ["Categories of Data", "User account and operational data"],
          ["Data Subjects", "Customer Users and business contacts"],
          ["Duration", "Duration of the MSA and applicable retention period"],
        ],
      },
    ],
  },
  {
    id: "annex-2",
    number: "Annex II",
    title: "Technical & Organisational Measures (Summary)",
    body: [
      {
        type: "p",
        text: "MiseOne maintains technical and organisational measures appropriate to the risks associated with the Services, including:",
      },
      {
        type: "ul",
        items: [
          "HTTPS/TLS encryption;",
          "encryption of stored data;",
          "Role-Based Access Control (RBAC);",
          "logical tenant isolation;",
          "audit logging;",
          "automated backups;",
          "controlled administrative access;",
          "regular security updates;",
          "incident response procedures.",
        ],
      },
      {
        type: "p",
        text: "The list may be updated from time to time provided that the overall level of protection is not materially reduced.",
      },
    ],
  },
  {
    id: "annex-3",
    number: "Annex III",
    title: "Approved Subprocessors",
    body: [
      { type: "p", text: "MiseOne maintains an up-to-date list of approved subprocessors." },
      { type: "p", text: "The current list is available separately and forms part of this DPA." },
    ],
  },
];

export const DataProcessingAgreement = () => (
  <LegalPage
    eyebrow="Legal"
    title="Data Processing Agreement"
    sections={sections}
    testId="dpa-page"
  />
);

export default DataProcessingAgreement;
